import { useState } from 'react';
import type { ChatMessage as ChatMessageType } from '../hooks/useChat';

interface ChatMessageProps {
  message: ChatMessageType;
  index: number;
}

/**
 * Single chat bubble. User messages right-aligned, assistant left-aligned.
 * Assistant messages render plain text with preserved newlines (no Markdown
 * parse — the LLM responses are short and citation-heavy, extra complexity
 * is not earned). Sources are collapsed by default; an aria-expanded toggle
 * reveals the list beneath the message body.
 */
export function ChatMessage({ message, index }: ChatMessageProps) {
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const isUser = message.role === 'user';

  return (
    <div
      data-testid={`aviation-demo-chat-message-${index}`}
      data-role={message.role}
      className={isUser ? 'flex justify-end' : 'flex justify-start'}
    >
      <div
        className={[
          'max-w-[80%] rounded-2xl px-4 py-3 shadow-sm',
          isUser
            ? 'bg-sky-600 text-white'
            : 'bg-white/80 dark:bg-slate-800/80 text-gray-900 dark:text-slate-100 backdrop-blur-sm',
        ].join(' ')}
      >
        {!isUser && (
          <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-1">
            Aviation RAG
          </div>
        )}
        <div
          className="whitespace-pre-wrap text-sm leading-relaxed"
          data-testid={`aviation-demo-chat-message-${index}-content`}
        >
          {message.content || (message.streaming ? <span className="inline-block animate-pulse text-gray-400">…</span> : '')}
        </div>

        {message.error && (
          <div
            className="mt-2 text-xs text-red-600 dark:text-red-400"
            data-testid={`aviation-demo-chat-message-${index}-error`}
          >
            error: {message.error}
          </div>
        )}

        {!isUser && message.sources && message.sources.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-slate-700">
            <button
              type="button"
              onClick={() => setSourcesOpen((o) => !o)}
              aria-expanded={sourcesOpen}
              className="text-xs font-medium text-sky-600 dark:text-sky-400 hover:underline focus:outline-none focus:ring-2 focus:ring-sky-500 rounded"
              data-testid={`aviation-demo-chat-message-${index}-sources-toggle`}
            >
              {sourcesOpen ? '▼' : '▶'} Sources ({message.sources.length})
            </button>
            {sourcesOpen && (
              <ul
                className="mt-2 space-y-1 text-xs text-gray-700 dark:text-slate-300"
                data-testid={`aviation-demo-chat-message-${index}-sources-list`}
              >
                {message.sources.map((src, i) => (
                  <li key={i} className="font-mono">• {src}</li>
                ))}
              </ul>
            )}
            {message.model && (
              <div className="mt-1 text-[10px] text-gray-400 dark:text-slate-500 font-mono">
                model: {message.model}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
